//Normal Tax Validation
export function validateNormalTax(tax: string): boolean {
  const taxRegex = /^[a-zA-Z0-9-]+$/;
  if (!taxRegex.test(tax)) {
    return false;
  }
  return true;
}

// Argentina Sample case
// 30-61295852-8
export function validateCUIT(cuit: string): boolean {
  const cuitRegex = /^(20|23|24|27|30|33|34)-\d{8}-\d{1}$/;
  if (!cuitRegex.test(cuit)) {
    return false;
  }

  const numbers = cuit.replace(/-/g, '');
  const coefficients = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < coefficients.length; i++) {
    sum += parseInt(numbers[i], 10) * coefficients[i];
  }

  const remainder = sum % 11;
  const checkDigit = remainder === 0 ? 0 : remainder === 1 ? 9 : 11 - remainder;

  return checkDigit === parseInt(numbers[10], 10);
}

// Argentina Tax Validation
export function formatCUIT(input: string): string {
  const digits = input.replace(/\D/g, '');
  if (digits.length !== 11) {
    return digits;
  }

  return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
}

// Mexico Tax Validation
export function formatRFC(input: string): string {
  const formatted = input.toUpperCase().replace(/[^A-Z0-9]/g, '');

  if (formatted.length > 13) {
    return formatted.slice(0, 13);
  }

  if (formatted.length === 12) {
    return `${formatted.slice(0, 3)}-${formatted.slice(3, 9)}-${formatted.slice(9)}`;
  } else if (formatted.length === 13) {
    return `${formatted.slice(0, 4)}-${formatted.slice(4, 10)}-${formatted.slice(10)}`;
  }

  return formatted;
}

// Mexico Sample case
// GODE561231GR8 -> success
// ABCD123456789 -> fail
export function validateRFC(rfc: string): boolean {
  const cleanedRFC = rfc.replace(/-/g, '');

  const individualRegex = /\b[A-Z][AEIOU][A-Z]{2}[0-9]{2}(?:0[1-9]|1[0-2])(?:[1-2][0-9]|0[1-9]|3[0-1])[A-Z0-9]{2}[0-9A-Z]\b/;
  const companyRegex = /\b[A-Z&Ñ]{3}[0-9]{2}(?:0[1-9]|1[0-2])(?:[1-2][0-9]|0[1-9]|3[0-1])[A-Z0-9]{2}[0-9A-Z]\b/;

  if (individualRegex.test(cleanedRFC) || companyRegex.test(cleanedRFC)) return true;

  return false;
}

// Brazil tax validation
// 111.444.777.35 SUCCESS
// 058.300.335-42 SUCCESS
export function validateCPF(cpf: string): boolean {
  cpf = cpf.replace(/\D/g, ''); // 모든 비숫자 문자 제거

  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false; // 길이 검사 및 모든 숫자가 같은 경우 거부

  const calculateDigit = (base: number) => {
    let sum = 0;
    for (let i = 0; i < base; i++) {
      sum += parseInt(cpf[i]) * (base + 1 - i);
    }
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const digit1 = calculateDigit(9);
  const digit2 = calculateDigit(10);

  return digit1 === parseInt(cpf[9]) && digit2 === parseInt(cpf[10]);
}

export const formatCPF = (input: string) => {
  // 숫자만 추출
  const numbers = input.replace(/\D/g, '');

  // 숫자를 CPF 포맷으로 변환
  let formatted = numbers;

  // XXX.XXX.XXX-XX 형식에 맞게 포맷
  if (numbers.length > 9) {
    formatted = `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
  } else if (numbers.length > 6) {
    formatted = `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}`;
  } else if (numbers.length > 3) {
    formatted = `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}`;
  } else {
    formatted = numbers;
  }

  return formatted;
};

// 통관용 Tax ID 국가별 설정
// 7개 대상국 모두 required
export interface TaxIdConfig {
  label: string;
  required: boolean;
  validate: (value: string) => boolean;
  format?: (value: string) => string;
}

export const TAX_ID_CONFIG: Record<string, TaxIdConfig> = {
  AR: { label: 'CUIT', required: true, validate: validateCUIT, format: formatCUIT },
  AT: { label: 'UID', required: true, validate: validateNormalTax },
  BR: { label: 'CPF', required: true, validate: validateCPF, format: formatCPF },
  CL: { label: 'RUT', required: true, validate: validateNormalTax },
  HN: { label: 'RTN', required: true, validate: validateNormalTax },
  MX: { label: 'RFC', required: true, validate: validateRFC, format: formatRFC },
  PE: { label: 'RUC', required: true, validate: validateNormalTax }
};
